import { InputAdornment, InputBase, styled } from '@mui/material'
import { CellPropsBase, CellTitle, CellValueArea, FormCell } from '.'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useWorkloadUnit } from '../../../../hooks/useWorkloadUnit'
import { WorkloadUnit } from '../../../../../lib/functions/workload'
import { intl } from '../../../../../i18n'
import { useNumberParser } from '../../../../hooks/useNumberParser'

type WorkloadCellProps = {
  value: number | undefined
  onChange?: (v: number | undefined) => void
} & CellPropsBase
export const WorkloadCell = ({
  label,
  cellWidth,
  value,
  onChange,
  editable = true,
  required = false,
  edited,
  validationErrors,
}: WorkloadCellProps) => {
  const {
    convertWorkloadFromHourToSelectedUnit: toDay,
    hoursPerSelectedUnit: hoursPerDay,
  } = useWorkloadUnit(WorkloadUnit.DAY)
  const {
    convertWorkloadFromHourToSelectedUnit: toMonth,
    hoursPerSelectedUnit: hoursPerMonth,
  } = useWorkloadUnit(WorkloadUnit.MONTH)
  const [hourInput, setHourInput] = useState('')
  const [dayInput, setDayInput] = useState('')
  const [monthInput, setMonthInput] = useState('')
  const changedByInput = useRef(false)
  const [hourIsComposing, setHourIsComposing] = useState(false)
  const [dayIsComposing, setDayIsComposing] = useState(false)
  const [monthIsComposing, setMonthIsComposing] = useState(false)

  const { round, parse } = useNumberParser()

  const hourToInputValue = useCallback(
    (value: number | undefined) => {
      return {
        hour: value,
        day: value ? round(toDay(value)) : undefined,
        month: value ? round(toMonth(value)) : undefined,
      }
    },
    [toDay, toMonth, round]
  )

  useEffect(() => {
    const { hour, day, month } = hourToInputValue(value)
    changedByInput.current = false
    setHourInput(hour ? hour + '' : '')
    setDayInput(day ? day + '' : '')
    setMonthInput(month ? month + '' : '')
  }, [value, hourToInputValue])

  const DEBOUNCE_DELAY = 800

  // Hour input value
  const onChangeHour = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setHourInput(e.target.value)
    changedByInput.current = true
  }, [])
  const debouncedHourInput = useDebounce(hourInput, DEBOUNCE_DELAY)
  useEffect(() => {
    if (hourIsComposing) return
    if (!changedByInput.current) return
    const normalized = parse(debouncedHourInput)
    const hourValue = round(normalized)
    onChange && onChange(hourValue)
    const { hour, day, month } = hourToInputValue(hourValue)
    setHourInput(typeof hour === 'number' ? hour + '' : '')
    setDayInput(typeof day === 'number' ? day + '' : '')
    setMonthInput(typeof month === 'number' ? month + '' : '')
  }, [
    debouncedHourInput,
    parse,
    round,
    onChange,
    hourIsComposing,
    hourToInputValue,
  ])

  // Day input value
  const onChangeDay = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDayInput(e.target.value)
    changedByInput.current = true
  }, [])
  const debouncedDayInput = useDebounce(dayInput, DEBOUNCE_DELAY)
  useEffect(() => {
    if (dayIsComposing) return
    if (!changedByInput.current) return
    const normalized = parse(debouncedDayInput)
    const hourValue = round(
      typeof normalized === 'number' ? normalized * hoursPerDay : undefined
    )
    onChange && onChange(hourValue)
    const { hour, day, month } = hourToInputValue(hourValue)
    setHourInput(typeof hour === 'number' ? hour + '' : '')
    setDayInput(typeof day === 'number' ? day + '' : '')
    setMonthInput(typeof month === 'number' ? month + '' : '')
  }, [
    debouncedDayInput,
    hoursPerDay,
    parse,
    round,
    onChange,
    dayIsComposing,
    hourToInputValue,
  ])

  // Month input value
  const onChangeMonth = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMonthInput(e.target.value)
    changedByInput.current = true
  }, [])
  const debouncedMonthInput = useDebounce(monthInput, DEBOUNCE_DELAY)
  useEffect(() => {
    if (monthIsComposing) return
    if (!changedByInput.current) return
    const normalized = parse(debouncedMonthInput)
    const hourValue = round(
      typeof normalized === 'number' ? normalized * hoursPerMonth : undefined
    )
    onChange && onChange(hourValue)
    const { hour, day, month } = hourToInputValue(hourValue)
    setHourInput(typeof hour === 'number' ? hour + '' : '')
    setDayInput(typeof day === 'number' ? day + '' : '')
    setMonthInput(typeof month === 'number' ? month + '' : '')
  }, [
    debouncedMonthInput,
    hoursPerMonth,
    parse,
    round,
    onChange,
    monthIsComposing,
    hourToInputValue,
  ])

  const onCompositionStartHour = useCallback(() => {
    setHourIsComposing(true)
  }, [])
  const hourInputValueRef = useRef<string>('')
  useEffect(() => {
    hourInputValueRef.current = hourInput
  }, [hourInput])
  const onCompositionEndHour = useCallback(() => {
    setHourIsComposing(false)
    changedByInput.current = true
    setHourInput(hourInputValueRef.current)
  }, [])

  const onCompositionStartDay = useCallback(() => {
    setDayIsComposing(true)
  }, [])
  const dayInputValueRef = useRef<string>('')
  useEffect(() => {
    dayInputValueRef.current = dayInput
  }, [dayInput])
  const onCompositionEndDay = useCallback(() => {
    setDayIsComposing(false)
    changedByInput.current = true
    setDayInput(dayInputValueRef.current)
  }, [])

  const onCompositionStartMonth = useCallback(() => {
    setMonthIsComposing(true)
  }, [])
  const monthInputValueRef = useRef<string>('')
  useEffect(() => {
    monthInputValueRef.current = monthInput
  }, [monthInput])
  const onCompositionEndMonth = useCallback(() => {
    setMonthIsComposing(false)
    changedByInput.current = true
    setMonthInput(monthInputValueRef.current)
  }, [])
  return (
    <FormCell cellWidth={cellWidth}>
      <CellTitle title={label} required={required} />
      <CellValueArea edited={edited} validationErros={validationErrors}>
        <TextInput
          value={hourInput}
          onChange={onChangeHour}
          disabled={!editable}
          inputProps={{
            style: { textAlign: 'right' },
          }}
          endAdornment={
            <WorkloadUnitAdornment
              unitLabel={intl.formatMessage({
                id: 'wbsItem.workload.unit.hour',
              })}
            />
          }
          onCompositionStart={onCompositionStartHour}
          onCompositionEnd={onCompositionEndHour}
        />
        <TextInput
          value={dayInput}
          onChange={onChangeDay}
          disabled={!editable}
          inputProps={{
            style: { textAlign: 'right' },
          }}
          endAdornment={
            <WorkloadUnitAdornment
              unitLabel={intl.formatMessage({
                id: 'wbsItem.workload.unit.day',
              })}
            />
          }
          onCompositionStart={onCompositionStartDay}
          onCompositionEnd={onCompositionEndDay}
        />
        <TextInput
          value={monthInput}
          onChange={onChangeMonth}
          disabled={!editable}
          inputProps={{
            style: { textAlign: 'right' },
          }}
          endAdornment={
            <WorkloadUnitAdornment
              unitLabel={intl.formatMessage({
                id: 'wbsItem.workload.unit.month',
              })}
            />
          }
          onCompositionStart={onCompositionStartMonth}
          onCompositionEnd={onCompositionEndMonth}
        />
      </CellValueArea>
    </FormCell>
  )
}

const TextInput = styled(InputBase)({})

const WorkloadUnitAdornment = ({ unitLabel }: { unitLabel: string }) => (
  <InputAdornment position="end">/{unitLabel}</InputAdornment>
)
